const FORM_ERRORS_ESP = {
  blank: "El campo no puede estar vacio.",
  maxLength: "El maximo de caracteres es ",
  minLength: "El minimo de caracteres es ",
  maxValue: "El valor maximo aceptado es",
  minValue: "El valor minimo aceptado es ",
  fieldType: "El campo debe contener ",
  spaces: "El campo NO debe contener espacios en blanco.",
};
const FIELD_TYPE_TO_ESP = {
  number: "Números",
  string: "Caracteres",
};
const FIELD_RULES_VALIDATION = {
  optional: "boolean",
  maxValue: "number",
  minValue: "number",
  maxLength: "number",
  minLength: "number",
  fieldType: ["number", "string"],
  noSpaces: "boolean",
  cantBeBlank: "boolean",
};
const reWhiteSpace = /\s/;

/**
 * Returns a NEW array sorted alphabetically (locale-aware, case-insensitive).
 * Works with arrays of objects (sorted by `key`) or arrays of strings.
 * Safe to use in computed properties / getters (does not mutate the input).
 */
export function alphabetized(array, key = "name") {
  const label = (item) =>
    item !== null && typeof item === "object"
      ? String(item?.[key] ?? "")
      : String(item ?? "");
  return [...(array ?? [])].sort((a, b) =>
    label(a).localeCompare(label(b), "es", {
      sensitivity: "base",
      numeric: true,
    }),
  );
}

export function sortArrayOfObjectsByAlphabet(
  arrayToSort,
  keyToSort,
  descending = false
) {
  /* This is a procedure, will affect permantly the given array */
  try {
    if (
      arrayToSort.length == 0 ||
      typeof arrayToSort?.[0]?.[keyToSort] != "string"
    ) {
      throw new Error("Not a valid array to sort");
    }
    // sortDirection = 1 -> ascending order;
    // sortDirection = -1 -> descending order;
    let sortDirection = 1;
    if (descending) {
      sortDirection = -1;
    }
    arrayToSort.sort(
      (a, b) =>
        sortDirection *
        a?.[keyToSort].toLowerCase().localeCompare(b?.[keyToSort].toLowerCase())
    );
  } catch (e) {
    console.log("Sort Array Objects failed");
    console.log(e);
    throw new Error("Sort Array Objects failed");
  }
}
export function validateForm(field, fieldRules, fieldOptional) {
  let detail = [];
  try {
    for (const ruleKey of Object.keys(fieldRules)) {
      if (!FIELD_RULES_VALIDATION?.[ruleKey]) {
        throw new Error("Invalid rule on Field Rules");
      }
      if (ruleKey == "fieldType") {
        if (
          !FIELD_RULES_VALIDATION?.[ruleKey].includes(
            typeof fieldRules?.[ruleKey]
          )
        ) {
          throw new Error("Invalid value type on Field Rules");
        }
      } else if (
        FIELD_RULES_VALIDATION?.[ruleKey] !== typeof fieldRules?.[ruleKey]
      ) {
        throw new Error("Invalid value type on Field Rules");
      }
    }
  } catch (e) {
    console.log("Field validation fail");
    console.log(e);
    throw new Error("Field validation fail");
  }

  if (field == "" && fieldOptional != undefined) {
    return detail;
  }
  if (fieldRules?.maxValue != undefined) {
    detail.push(
      field > fieldRules?.maxValue
        ? FORM_ERRORS_ESP.maxValue + ` ${fieldRules?.maxValue}`
        : true
    );
  }
  if (fieldRules?.minValue != undefined) {
    detail.push(
      field < fieldRules?.minValue
        ? FORM_ERRORS_ESP.minValue + ` ${fieldRules?.minValue}`
        : true
    );
  }
  if (fieldRules?.maxLength != undefined) {
    detail.push(
      field.length > fieldRules?.maxLength ? true : FORM_ERRORS_ESP.maxLength
    );
  }
  if (fieldRules?.minLength != undefined) {
    detail.push(
      field.length > fieldRules?.minLength ? true : FORM_ERRORS_ESP.minLength
    );
  }
  if (fieldRules?.fieldType != undefined) {
    detail.push(
      typeof field != fieldRules?.fieldType
        ? FORM_ERRORS_ESP.fieldType + FIELD_TYPE_TO_ESP[fieldRules?.fieldType]
        : true
    );
  }
  if (fieldRules?.noSpaces != undefined) {
    detail.push(!reWhiteSpace.test(field) ? true : FORM_ERRORS_ESP.spaces);
  }
  if (fieldRules?.cantBeBlank != undefined) {
    detail.push(field !== "" ? true : FORM_ERRORS_ESP.blank);
  }
  return detail;
}

export function roundToX(num, X) {
  return +(Math.round(num + "e+" + X) + "e-" + X);
}

/**
 * Convert a date input (typically a native <input type="date"> value like
 * "YYYY-MM-DD") into an ISO string, guarding against the implausible years a
 * native date picker emits when a 2-digit year is typed (e.g. "0026-12-01").
 * Any year below 1000 is normalized to 2000 + its last two digits (0026 ->
 * 2026). Valid 4-digit years are left untouched, so this is a safe drop-in for
 * `new Date(x).toISOString()`. Returns null for empty/invalid input.
 */
export function toSafeISODate(dateInput) {
  if (!dateInput) {
    return null;
  }
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) {
    return null;
  }
  // Use UTC accessors so normalizing ancient years does not drift the day via
  // historical local-mean-time offsets.
  const year = date.getUTCFullYear();
  if (year < 1000) {
    date.setUTCFullYear(2000 + (year % 100));
  }
  return date.toISOString();
}

/**
 * Select variable sharecropping percent for a yield using half-open brackets
 * [floor, ceiling), clamping to the lowest bracket below min floor and the
 * highest bracket at/above max ceiling (matches backend logic).
 */
export function getVariableSharecropPercent(schedule, dynamicYield) {
  if (!schedule?.length || dynamicYield == null) {
    return 0;
  }

  const minFloor = Math.min(...schedule.map((row) => row.floor));
  const maxCeiling = Math.max(...schedule.map((row) => row.ceiling));
  const maxFloor = Math.max(...schedule.map((row) => row.floor));
  const minFloorRow = schedule.find((row) => row.floor === minFloor);
  const maxFloorRow = schedule.find((row) => row.floor === maxFloor);

  if (dynamicYield < minFloor) {
    return minFloorRow?.percent ?? 0;
  }
  if (dynamicYield >= maxCeiling) {
    return maxFloorRow?.percent ?? 0;
  }

  return (
    schedule.find(
      (row) => dynamicYield >= row.floor && dynamicYield < row.ceiling,
    )?.percent ?? 0
  );
}

export function addOneMonth(date, deltaDays) {
  let nextMonth = date.getMonth() + 1;
  if (nextMonth == 12) {
    nextMonth = 0;
  }
  date.setMonth(date.getMonth() + 1);
  // Add missing extra days
  date.setDate(date.getDate() + deltaDays);
  // Now delta is 0
  deltaDays = 0;

  // Check overflows
  if (date.getMonth() > nextMonth) {
    // This will remove the overflow days and go to the last day of the previous month
    console.log("date overflow up");
    deltaDays = date.getDate();
    date.setDate(date.getDate() - date.getDate());
  }
  if (date.getMonth() < nextMonth) {
    // This will add the missing days and go to the first day of the next month
    console.log("date overflow below");
    let newDate = new Date(date.getFullYear(), date.getMonth() + 1, 1);
    const diffTime = Math.abs(newDate - date);
    deltaDays = -Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    date = newDate;
  }
  return [date, deltaDays];
}
export function getMonthRange(numOfMonths, date) {
  let sd = date;
  let dateToAddOneMonth = new Date(date);
  let dateRange = [sd];
  let deltaDays = 0;
  for (let i = 1; i < numOfMonths; i++) {
    [dateToAddOneMonth, deltaDays] = addOneMonth(dateToAddOneMonth, deltaDays);
    dateRange.push(new Date(dateToAddOneMonth));
  }
  return dateRange;
}
// Helper function to identify ID fields that should not be formatted
function isIdField(fieldName) {
  const idPatterns = [
    /id$/i,                    // ends with 'id' (case insensitive)
    /^id$/i,                   // exactly 'id' (case insensitive)
    /Id$/,                     // ends with 'Id' (camelCase)
    /ID$/,                     // ends with 'ID' (uppercase)
    /_id$/i,                   // ends with '_id' (snake_case)
    /^.*[Ii]d$/,              // any field ending with 'id' or 'Id'
    /engagementId/i,          // specific engagement ID patterns
    /activitySliceId/i,       // specific activity slice ID patterns
    /draftActivitySliceId/i,  // draft activity slice ID patterns (NEW - this was missing!)
    /campaignId/i,            // specific campaign ID patterns
    /draftId/i,               // draft ID patterns
    /draftEngagementId/i,     // draft engagement ID patterns
    /establishmentId/i,       // establishment ID patterns
    /sliceId/i,               // slice ID patterns
    /version$/i,              // version numbers
    /.*Id$/,                  // any field ending with 'Id' (broader pattern)
  ];
  
  return idPatterns.some(pattern => pattern.test(fieldName));
}

export function formatRowsToLocaleString(rows) {
  rows = rows.map((obj) => {
    let newObj = {};
    Object.keys(obj).forEach((key) => {
      if (typeof obj[key] === "number" && !isIdField(key)) {
        newObj[key] = parseToLocaleString(obj[key]);
      } else {
        newObj[key] = obj[key];
      }
    });
    return newObj;
  });
  return rows;
}
export function parseToLocaleString(value) {
  // Handle NaN, null, undefined, Infinity
  if (value === null || value === undefined || 
      (typeof value === 'number' && (!isFinite(value) || isNaN(value)))) {
    return '-';
  }
  return value?.toLocaleString("en-US", {
    timeZone: "America/Argentina/Buenos_Aires",
  });
}
